// 자체 에러 추적 — Sentry 대체
// /api/v1/monitoring/error로 직접 전송. @sentry/react 의존성 제거.
//
// 프라이버시:
//  - PII 자동 수집 X
//  - Authorization/매장 ID/이미지 등 마스킹
//
// 사용법: main.tsx에서 initSentry() 호출 (함수명은 호환성 유지).

import { Component, type ReactNode, type ErrorInfo } from 'react'

const SENSITIVE_KEYS = new Set([
  'image_base64', 'image', 'gemini_api_key', 'supabase_service_key',
  'password', 'card_number', 'cvc', 'rrn',
])

let _userId: string | null = null

function _sendError(
  message: string,
  level: 'error' | 'warning' | 'info' = 'error',
  stack?: string,
  extra?: Record<string, unknown>,
): void {
  if (import.meta.env.DEV) {
    console.debug('[error-tracker]', level, message)
    return
  }

  // PII 필터
  const safeExtra: Record<string, unknown> = {}
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      safeExtra[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : v
    }
  }
  if (_userId) {
    safeExtra.user_id = _userId
  }

  fetch('/api/v1/monitoring/error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: message.slice(0, 2000),
      level,
      stack_trace: stack?.slice(0, 10000),
      endpoint: window.location.pathname,
      extra: Object.keys(safeExtra).length > 0 ? safeExtra : undefined,
    }),
    keepalive: true,
  }).catch(() => {})
}

let _initialized = false

export function initSentry(): boolean {
  if (_initialized) return true

  // 글로벌 에러 핸들러 등록
  window.addEventListener('error', (event) => {
    _sendError(
      event.message || 'Uncaught Error',
      'error',
      event.error?.stack,
      { filename: event.filename, lineno: event.lineno, colno: event.colno },
    )
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason)
    const stack = reason instanceof Error ? reason.stack : undefined
    _sendError(`Unhandled Promise: ${message}`, 'error', stack)
  })

  _initialized = true
  return true
}

/**
 * 사용자 ID 설정 (로그인 후 호출).
 */
export function setSentryUser(userId: string | null) {
  _userId = userId
}

/**
 * 수동 에러 리포팅.
 */
export function captureError(error: Error, extra?: Record<string, unknown>) {
  _sendError(error.message, 'error', error.stack, extra)
}

/**
 * 에러 바운더리 — Sentry.ErrorBoundary 대체.
 */
interface ErrorBoundaryProps {
  fallback: (props: { error: Error }) => ReactNode
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

class SelfErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    _sendError(error.message, 'error', error.stack, {
      componentStack: info.componentStack?.slice(0, 5000),
    })
  }

  render() {
    if (this.state.error) {
      return this.props.fallback({ error: this.state.error })
    }
    return this.props.children
  }
}

export const SentryErrorBoundary = SelfErrorBoundary
